import React, { createContext, useContext, useReducer, useEffect } from 'react'
import toast from 'react-hot-toast'
import { cartService } from '../services/api'

const CartContext = createContext()

const initialState = {
  items: [],
  loading: false,
  total: 0,
  itemCount: 0,
}

const cartReducer = (state, action) => {
  switch (action.type) {
    case 'SET_LOADING':
      return {
        ...state,
        loading: action.payload,
      }
    case 'LOAD_CART':
      return {
        ...state,
        items: action.payload.items || [],
        total: parseFloat(action.payload.summary?.total || 0),
        itemCount: parseInt(action.payload.summary?.total_items || 0),
        loading: false,
      }
    case 'ADD_TO_CART':
      return {
        ...state,
        loading: false,
      }
    case 'UPDATE_CART_COUNT':
      return {
        ...state,
        itemCount: action.payload,
      }
    case 'CLEAR_CART':
      return {
        ...state,
        items: [],
        total: 0,
        itemCount: 0,
        loading: false,
      }
    case 'CART_ERROR':
      return {
        ...state,
        loading: false,
      }
    default:
      return state
  }
}

export const CartProvider = ({ children }) => {
  const [state, dispatch] = useReducer(cartReducer, initialState)

  useEffect(() => {
    loadCart()
    loadCartCount()
  }, [])

  const loadCart = async () => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true })
      const response = await cartService.getCart()
      dispatch({ type: 'LOAD_CART', payload: response })
    } catch (error) {
      console.error('Load cart error:', error)
      dispatch({ type: 'CART_ERROR' })
      // Don't show error toast for cart loading as it might be empty
    }
  }

  const loadCartCount = async () => {
    try {
      const count = await cartService.getCartCount()
      dispatch({ type: 'UPDATE_CART_COUNT', payload: count })
    } catch (error) {
      // Silent error for cart count
    }
  }

  const addToCart = async (productId, quantity = 1, selectedAttributes = {}) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true })
      await cartService.addToCart(productId, quantity, selectedAttributes)
      dispatch({ type: 'ADD_TO_CART' })
      toast.success('Item added to cart!')
      await loadCart()
      await loadCartCount()
    } catch (error) {
      console.error('Add to cart error:', error)
      dispatch({ type: 'CART_ERROR' })
      toast.error(error.message || 'Failed to add item to cart')
    }
  }

  const updateCartItem = async (itemId, quantity) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true })
      await cartService.updateCartItem(itemId, quantity)
      toast.success('Cart updated!')
      await loadCart()
      await loadCartCount()
    } catch (error) {
      dispatch({ type: 'CART_ERROR' })
      toast.error(error.message || 'Failed to update cart')
    }
  }

  const removeFromCart = async (itemId) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true })
      await cartService.removeFromCart(itemId)
      toast.success('Item removed from cart')
      await loadCart()
      await loadCartCount()
    } catch (error) {
      dispatch({ type: 'CART_ERROR' })
      toast.error(error.message || 'Failed to remove item')
    }
  }

  const clearCart = async () => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true })
      await cartService.clearCart()
      dispatch({ type: 'CLEAR_CART' })
      toast.success('Cart cleared!')
    } catch (error) {
      dispatch({ type: 'CART_ERROR' })
      toast.error(error.message || 'Failed to clear cart')
    }
  }

  const value = {
    items: state.items,
    total: state.total,
    itemCount: state.itemCount,
    loading: state.loading,
    addToCart,
    updateCartItem,
    removeFromCart,
    clearCart,
    loadCart,
    loadCartCount,
  }

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>
}

// Export the hook separately to be Fast Refresh compatible
const useCart = () => {
  const context = useContext(CartContext)
  if (!context) {
    throw new Error('useCart must be used within a CartProvider')
  }
  return context
}

export { useCart }
